import React, { FC, PropsWithChildren, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { useModal } from '@layout/business/Layout.utils';
import classnames from 'classnames';
import { ModalProps } from './Modal.types';
import styles from './Modal.module.scss'

interface Props extends Omit<ModalProps, 'close'> {
  elementId?: number;
  onConfirm: (id?: string | number) => void;
  onDecline: (id?: string | number) => void;
  confirmI18nKey?: string;
  declineI18nKey?: string;
  inverseButtonColor?: boolean;
}

const ConfirmationModal: FC<PropsWithChildren<Props>> = ({
  elementId,
  onConfirm,
  onDecline,
  children,
  className,
  visible,
  confirmI18nKey = 'common.yes',
  declineI18nKey = 'common.no',
  inverseButtonColor,
  ...rest
}) => {
  const { show, hide } = useModal();
  const rootClassName = classnames(styles['confirmation-modal'], className);

  function handleDecline() {
    onDecline(elementId);
  }

  function handleConfirm() {
    onConfirm(elementId);
  }

  useEffect(() => {
    if (visible) {
      show({
        className: rootClassName,
        close: handleDecline,
        ...rest,
        actions: (
          <div className={styles["confirmation-modal-buttons"]}>
            {inverseButtonColor ? (
              <>
                <PrimaryButton isSmall onClick={handleDecline} textProps={{ i18nKey: declineI18nKey }} />
                <SecondaryButton isSmall onClick={handleConfirm} textProps={{ i18nKey: confirmI18nKey }} />
              </>
            ) : (
              <>
                <PrimaryButton isSmall onClick={handleConfirm} textProps={{ i18nKey: confirmI18nKey }} />
                <SecondaryButton isSmall onClick={handleDecline} textProps={{ i18nKey: declineI18nKey }} />
              </>
            )}
          </div>
        ),
        children: <Text variant="p">{children}</Text>,
      });
    } else {
      hide();
    }
  }, [visible]);

  return null;
};

export { ConfirmationModal };
